import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetExplodingFeeScheduleByIdQuery,
  useCreateExplodingFeeScheduleMutation,
} from "store/queries/explodingFeeSchedule";

import {
  ExplodingFeeScheduleForm,
  IExplodingFeeScheduleFormData,
  INITIAL_FORM_DATA,
} from "./components/ExplodingFeeScheduleForm";

export function CopyExplodingFeeSchedulePage() {
  useDocumentTitle("Copy Exploding Fee Schedule");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateExplodingFeeScheduleMutation();
  const { data: rulesetData, isLoading: rulesetLoading } =
    useGetExplodingFeeScheduleByIdQuery(id!);

  const [formData, setFormData] =
    useState<IExplodingFeeScheduleFormData>(INITIAL_FORM_DATA);

  useEffect(() => {
    if (rulesetData?.data) {
      const r = rulesetData.data;
      setFormData({
        providerId: r.providerId,
        payerId: r.payerId,
        cptCodes: r.cptCodes,
        totalRateInCents: r.totalRateInCents,
        groupName: r.groupName ? `${r.groupName} (Copy)` : "",
      });
    }
  }, [rulesetData]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await createRuleset({
        providerId: formData.providerId!,
        payerId: formData.payerId!,
        cptCodes: formData.cptCodes,
        totalRateInCents: formData.totalRateInCents!,
        groupName: formData.groupName.trim() || undefined,
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Exploding fee schedule ruleset copied successfully.",
      });
      navigate("/payer-rules?tab=3");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message ||
          "Failed to copy exploding fee schedule ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (rulesetLoading) return <LoadingSpinner />;

  return (
    <ExplodingFeeScheduleForm
      title="Copy Exploding Fee Schedule Ruleset"
      submitLabel="Create Copy"
      submitIcon="pi pi-copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
